import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { useUpdateDiary } from "../../../hooks/CALENDAR/useUpdateDiary";
import { useDeleteDiary } from "../../../hooks/CALENDAR/useDeleteDiary";
import { useUsers } from "../../../hooks/useGetUsers";
import toast from "react-hot-toast";

const toDateStr = (iso) => (iso ? iso.slice(0, 10) : "");
const toTimeStr = (iso) => (iso && iso.length >= 16 ? iso.slice(11, 16) : "");

const EditAgendaEvent = ({ event, show, onHide }) => {
  const [label, setLabel] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [heureDebut, setHeureDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [heureFin, setHeureFin] = useState("");
  const [description, setDescription] = useState("");
  const [participants, setParticipants] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { mutate: updateDiary, isPending: isUpdating } = useUpdateDiary();
  const { mutate: deleteDiary, isPending: isDeleting } = useDeleteDiary();
  const { data: usersData } = useUsers();

  const userOptions =
    usersData?.data?.map((u) => ({
      value: u.id,
      label: `${u.surname} ${u.name}`,
    })) || [];

  useEffect(() => {
    if (!event) return;
    setLabel(event.label || "");
    setDateDebut(toDateStr(event.start_date));
    setHeureDebut(toTimeStr(event.start_date));
    setDateFin(toDateStr(event.end_date));
    setHeureFin(toTimeStr(event.end_date));
    setDescription(event.description || "");
    setParticipants(
      (event.participants || []).map((p) => ({
        value: p.id,
        label: `${p.surname} ${p.name}`,
      }))
    );
    setConfirmDelete(false);
  }, [event]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const startDatetime = heureDebut
      ? `${dateDebut}T${heureDebut}:00`
      : `${dateDebut}T00:00:00`;

    const endDatetime =
      dateFin && heureFin
        ? `${dateFin}T${heureFin}:00`
        : dateFin
        ? `${dateFin}T00:00:00`
        : null;

    const payload = {
      label,
      start_date: startDatetime,
      end_date: endDatetime,
      description: description || null,
      participant_ids: participants.map((p) => p.value),
    };

    updateDiary(
      { id: event.id, data: payload },
      {
        onSuccess: () => {
          toast.success("Événement modifié");
          onHide();
        },
        onError: (err) => {
          toast.error(err.message || "Erreur lors de la modification");
        },
      }
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteDiary(event.id, {
      onSuccess: () => {
        toast.success("Événement supprimé");
        onHide();
      },
      onError: (err) => {
        toast.error(err.message || "Erreur lors de la suppression");
      },
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="xs">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="modal-title text-client">Modifier l'événement</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col>
              <Form.Control
                type="text"
                placeholder="Titre *"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Date début *</Form.Label>
              <Form.Control
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                required
              />
            </Col>
            <Col>
              <Form.Label className="text-muted">Heure début</Form.Label>
              <Form.Control
                type="time"
                value={heureDebut}
                onChange={(e) => setHeureDebut(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Date fin</Form.Label>
              <Form.Control
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
              />
            </Col>
            <Col>
              <Form.Label className="text-muted">Heure fin</Form.Label>
              <Form.Control
                type="time"
                value={heureFin}
                onChange={(e) => setHeureFin(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label className="text-muted">Participant(s)</Form.Label>
              <Select
                isMulti
                options={userOptions}
                value={participants}
                onChange={setParticipants}
                styles={customSelectStyles}
                placeholder="Sélectionner..."
                noOptionsMessage={() => "Aucun utilisateur"}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant={confirmDelete ? "danger" : "outline-danger"}
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting
              ? "Suppression..."
              : confirmDelete
              ? "Confirmer la suppression"
              : "Supprimer"}
          </Button>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={onHide}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" disabled={isUpdating}>
              {isUpdating ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditAgendaEvent;
